/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  Suspense,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import socketio from 'socket.io-client';
import {
  BrowserRouter as Router,
  Switch,
  Route,
} from 'react-router-dom';
import socketEvents from 'utils/sockets';
import { getModelPermission, getPermission, isStaff } from 'utils/permission/index';
// Routes
import Login from 'views/Auth/Login/index';
import ForgotPassword from 'views/Auth/ForgotPassword';
import PasswordRecovery from 'views/Auth/PasswordRecovery';
import ActivarCuenta from 'views/Auth/ActivarCuenta';
import RestablecerContrasena from 'views/Auth/RestablecerContrasena';
import CancelarRestablecerContrasena from 'views/Auth/CancelarRestablecerContrasena';
import Recibos from 'views/Recaudacion/Caja/Recibos';
// Components
import AuthRoute from 'components/AuthRoute';
import { getFlattedRoutes, getRoutes } from 'routes/index';
import { getLadas, getUserData } from 'api/usuarios';
import {
  Button,
  Result,
  Row,
  message,
} from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import { ErrorBoundary } from 'react-error-boundary';
import BusquedaDeRecibos from 'views/Recaudacion/BusquedaDeRecibos';
import ValidacionDeTramites from 'views/Tramites/ValidacionDeTramites';
import { getLlavesDePredio } from 'api/catastro/llavesDePredios';
import {
  getCatalogoDePadrones,
  getEstadosGlobales,
  getCatalogoDePadronesPublic,
  getCatalogoDePaises,
} from 'api/catalogos';
import { createEmptyAxiosInstance, logout } from 'utils/api';
import checkToken from 'api/utils/checkToken';
import { SET_AVAILABLE } from 'store/reducers/app';
import { toMoment } from 'utils/normalizers';
import { modelCrud as modelVerificacion } from 'components/Direccion/Verificada';
import Layout from 'components/Layout';
import NotFound from 'components/Layout/NotFound';
import { logger } from 'utils/debug';
import ValidacionDeConstancias from 'views/Tramites/ValidacionDeConstancias';
import getAllPages from './utils/getAllPages';
import { SET_PAISES } from './store/reducers/catalogs';

const {
  REACT_APP_SOCKETS_URL: SOCKETS_URL,
  NODE_ENV,
  REACT_APP_ENV: ENV,
  REACT_APP_ENV_NAME: ENV_NAME,
} = process.env;

const toLongFormat = (date) => date.format('dddd, D ** MMMM hh:mm a').replace('**', 'de');

const momentInTimeZone = (date, timeZone) => toMoment(date
  .toLocaleString('en-US', { timeZone }), ['MM/DD/YYYY, h:mm:ss A']);

const PROD = ENV === 'p';
const DAYS = { SATURDAY: '6', SUNDAY: '0' };

const defaultSchedules = {
  nayarit: {
    START: {
      HOUR: 6,
      MINUTES: 45,
    },
    END: {
      HOUR: 19,
      MINUTES: 45,
    },
  },
};

const schedules = {
  nayarit: {
    [DAYS.SATURDAY]: {
      START: {
        HOUR: 8,
        MINUTES: 45,
      },
      END: {
        HOUR: 14,
        MINUTES: 45,
      },
    },
  },
};

const tz = {
  nayarit: 'america/mazatlan',
};

const scheduleConfigAvailable = !!defaultSchedules[ENV_NAME]
    && !!schedules[ENV_NAME] && !!tz[ENV_NAME];
const useSchedule = PROD && scheduleConfigAvailable;

const dateGetter = async (url, prop) => {
  try {
    const res = await createEmptyAxiosInstance().get(url);
    if (res) {
      return res.data[prop];
    }
  } catch (error) {
    return null;
  }
  return null;
};

const getScheduleConfig = async () => {
  if (!useSchedule) {
    return [true];
  }
  const date = (await Promise.all([
    // dateGetter('https://worldtimeapi.org/api/timezone/America/Mazatlan', 'datetime'),
    // dateGetter('https://timeapi.io/api/TimeZone/zone?timeZone=America/Mazatlan', 'currentlocaltime'),
    dateGetter('https://timeapi.io/api/time/current/zone?timeZone=America%2FMazatlan', 'dateTime'),
  ])).find((e) => e);
  if (!date) {
    return [false];
  }
  const localDate = new Date(date);
  const nowMoment = momentInTimeZone(localDate, tz[ENV_NAME]);
  const dayOfWeek = nowMoment.format('d');
  const isAM = nowMoment.format('a') === 'am';
  const defaultSchedule = defaultSchedules[ENV_NAME];
  const schedule = schedules[ENV_NAME];
  const daySchedule = schedule[dayOfWeek] || defaultSchedule;
  const begin = nowMoment.clone().set('h', daySchedule.START.HOUR).set('m', daySchedule.START.MINUTES);
  const end = nowMoment.clone().set('h', daySchedule.END.HOUR).set('m', daySchedule.END.MINUTES);
  const isAvailable = dayOfWeek !== DAYS.SUNDAY && nowMoment.isBetween(begin, end);
  if (isAvailable) {
    return [isAvailable];
  }
  const daysToAdd = (dayOfWeek === DAYS.SATURDAY && 2) || (!isAM && 1) || 0;
  const nextLabolalDay = nowMoment.clone().add(daysToAdd, 'day');
  const nextLabolalDayDayOfWeek = nextLabolalDay.format('d');
  const nextLabolalDaySchedule = schedule[nextLabolalDayDayOfWeek] || defaultSchedule;
  nextLabolalDay.set('h', nextLabolalDaySchedule.START.HOUR);
  nextLabolalDay.set('m', nextLabolalDaySchedule.START.MINUTES);
  const _nextLabolalDay = toLongFormat(nextLabolalDay);
  return [isAvailable, _nextLabolalDay];
};

const AppRouter = () => {
  const dispatch = useDispatch();
  const { available } = useSelector(({ app }) => app);
  const { periodoFiscalSelected, token, user } = useSelector(({ auth }) => auth);
  const addressPermission = useMemo(
    () => getModelPermission('direccion', [modelVerificacion]),
    [user?.permisos],
  );
  const io = useRef();
  const timeoutRef = useRef();
  const [nextLaboral, setNextLaboral] = useState();
  const [loading, setLoading] = useState(false);

  const clearRef = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  };

  useEffect(() => {
    const onMouseMove = () => {
      if (NODE_ENV !== 'development') {
        clearRef();
        timeoutRef.current = setTimeout(() => {
          logout();
          message.info('Sesión cerrada por inactividad', 0);
        }, 1000 * 60 * 60);
      }
    };
    if (token && !timeoutRef.current) {
      window.addEventListener('mousemove', onMouseMove);
      onMouseMove();
    } else if (!token) {
      clearRef();
      window.removeEventListener('mousemove', onMouseMove);
    }
    return () => {
      if (timeoutRef.current && token) {
        clearRef();
        window.removeEventListener('mousemove', onMouseMove);
      }
    };
  }, [token]);

  useEffect(() => {
    if (token && SOCKETS_URL) {
      // Cerrar conexión anterior si existe
      if (io.current) {
        io.current.disconnect();
      }
      // Crear nueva conexión
      io.current = socketio(SOCKETS_URL, {
        auth: { token },
      });
    } else if (io.current) {
      // Cerrar conexión si no hay token o URL
      io.current.disconnect();
      io.current = null;
    }
    socketEvents(io.current);

    // Cleanup function
    return () => {
      if (io.current) {
        io.current.disconnect();
        io.current = null;
      }
    };
  }, [token, SOCKETS_URL]);

  useEffect(() => {
    const updateInfo = async () => {
      await checkToken();
      const [paises] = await Promise.all([
        getAllPages(getCatalogoDePaises),
        getUserData(),
        getEstadosGlobales(),
        getLlavesDePredio(),
        getLadas(),
      ]);
      dispatch({
        type: SET_PAISES,
        payload: paises,
      });
    };

    const checkIn = async () => {
      setLoading(true);
      const [isAvailable, _nextLabolalDay] = await getScheduleConfig(setLoading);
      setNextLaboral(_nextLabolalDay);
      dispatch({
        type: SET_AVAILABLE,
        payload: isAvailable,
      });
      setTimeout(() => {
        setLoading(false);
      });
    };
    checkIn();
    if (token && available) {
      getCatalogoDePadrones();
      updateInfo();
    } else {
      getCatalogoDePadronesPublic();
    }
    localStorage.setItem('allRoutes', JSON.stringify(getRoutes(false)));
  }, []);

  const routeToJSX = (route) => {
    const permission = getPermission(route.permissionConfig);
    if (route.withoutLayout) {
      return (
        <AuthRoute path={route.path} key={route.path} type="private" exact>
          <route.component
            {...route.componentProps}
            permission={permission}
            addressPermission={addressPermission}
          />
        </AuthRoute>
      );
    }
    return (
      <AuthRoute path={route.path} key={route.path} type="private" exact>
        <Layout>
          <route.component
            {...route.componentProps}
            permission={permission}
            addressPermission={addressPermission}
          />
        </Layout>
      </AuthRoute>
    );
  };

  const routes = useMemo(() => {
    const _routes = getRoutes();
    const filtered = periodoFiscalSelected?.vigente
      ? _routes : _routes.filter((e) => e.title !== 'Recaudación');
    const flatted = getFlattedRoutes(filtered);
    const routesAsJSX = flatted.map(routeToJSX);
    return routesAsJSX;
  }, [user?.permisos, periodoFiscalSelected]);

  if (loading) {
    return null;
  }
  if (!available) {
    return (
      <Row style={{ width: '100vw', height: '100vh' }} justify="center" align="middle">
        <Result
          status="500"
          title={(
            <>
              Intento de uso del sistema
              <br />
              <b>
                Fuera del horario de Operación
              </b>
            </>
          )}
          subTitle={!!nextLaboral && (
            <>
              Disponible
              <b>
                {` el ${nextLaboral}`}
              </b>
            </>
          )}
        />
      </Row>
    );
  }

  // eslint-disable-next-line react/prop-types
  const ErrorFallback = ({ error }) => {
    useEffect(() => {
      logger(error);
    }, []);

    const defMessage = 'Ocurrió un problema que detuvo el funcionamiento normal de la aplicación, nosotros nos pondremos en contacto con sistemas para que se solucione lo antes posible';

    const showMessage = process.env.NODE_ENV === 'development' || isStaff();
    return (
      <Result
        subTitle={defMessage}
        status="warning"
        extra={(
          <>
            {/* eslint-disable-next-line react/prop-types */}
            {showMessage && error.message}
            {showMessage && <br />}
            <Button
              icon={<DashboardOutlined />}
              onClick={() => {
                window.location.href = '/dashboard';
              }}
            >
              Regresar al Dashboard
            </Button>
          </>
      )}
      />
    );
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={null}>
        <Router>
          <div className="app-container">
            <Switch>
              {/* Layout routes */}
              {routes}
              {/* Guest routes */}
              <AuthRoute path="/" type="guest" exact>
                <Login />
              </AuthRoute>
              <AuthRoute path="/recuperar-contrasena" type="guest" exact>
                <ForgotPassword />
              </AuthRoute>
              <AuthRoute path="/recuperacion-de-contrasena" type="guest" exact>
                <PasswordRecovery />
              </AuthRoute>
              <AuthRoute path="/activar-cuenta" type="guest" exact>
                <ActivarCuenta />
              </AuthRoute>
              <AuthRoute path="/restablecer-contrasena" type="guest" exact>
                <RestablecerContrasena />
              </AuthRoute>
              <AuthRoute path="/cancelar-restablecer-contrasena" type="guest" exact>
                <CancelarRestablecerContrasena />
              </AuthRoute>
              <Route path="/recibos" exact>
                <BusquedaDeRecibos />
              </Route>
              <Route path="/constancias" exact>
                <ValidacionDeConstancias />
              </Route>
              <Route path="/tramites" exact>
                <ValidacionDeTramites />
              </Route>
              <AuthRoute path="/recibos-de-caja" type="private" exact>
                <Recibos />
              </AuthRoute>
              <Route path="*">
                <NotFound />
              </Route>
            </Switch>
          </div>
        </Router>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRouter;
